import { useState, useEffect, useContext } from 'react';
import { Context } from '@components/Context';
import { useAccountStore } from '@components/dashboard/context/UserDashboardContext';
import { manageHealth } from '@components/dashboard/hooks/manageHealth';
import { GeneratedPPCData } from '@components/dashboard/utils/periodPainConsultation';
import { checkTemporaryStorage, checkReportStorage } from '@components/dashboard/utils/periodPainReport';
import { ProductCode } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { isProductionEnvironment } from '@utils/environment';
import { useSearchParameter } from '@utils/useSearchParameter';
import { managePPC } from './managePPC';
import { usePpcQuestionnairePdfData } from './usePpcQuestionnairePdfData';

export const usePeriodPainReportData = () => {
  const unlockByUrl = useSearchParameter('unlocked');
  const shouldUnlockByUrl = unlockByUrl && !isProductionEnvironment();

  const userInfo = useAccountStore((accountState) => accountState.userInfo);
  const purchases = useAccountStore((accountState) => accountState.purchases);

  const { state } = useContext(Context);
  const { firebaseAuth, firebaseLoaded, loggedIn } = state;
  const { country } = useCountry();

  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(shouldUnlockByUrl ? !Number(unlockByUrl) : true);
  const [painReportData, setPainReportData] = useState<GeneratedPPCData | null>(null);
  const [noPainReportData, setNoPainReportData] = useState(false);

  const { getPublicQuestionnaire } = managePPC().useGetQuestionnaire();
  const { getCompletedInsight, createInsight } = manageHealth().useInsight();
  const { getConsultations } = managePPC().useConsultations();

  usePpcQuestionnairePdfData(painReportData);

  useEffect(() => {
    if (!firebaseLoaded || loggedIn === null) return;
    loadPeriodPainReport();
  }, [loggedIn, firebaseLoaded]);

  useEffect(() => {
    checkIfReportLocked();
  }, [purchases]);

  const loadPeriodPainReport = async () => {
    let data = null;
    try {
      const googleIdToken = await firebaseAuth?.currentUser?.getIdToken();
      const questionnaireData = await getPublicQuestionnaire();
      const questionnaireRevisionId = questionnaireData?.id;

      if (userInfo.isMfaEnabled) {
        try {
          data = await checkReportStorage(questionnaireRevisionId, googleIdToken ?? '', getCompletedInsight, country);
        } catch (error) {
          // Continue execution even if report storage check fails
        }
      }

      if (!data) {
        try {
          data = await checkTemporaryStorage(questionnaireRevisionId, googleIdToken ?? '', userInfo, getConsultations, createInsight, country);
        } catch (error) {
          // Continue execution even if temporary storage check fails
        }
      }

      if (data) {
        setPainReportData(data);
      } else {
        setNoPainReportData(true);
      }
    } catch (error) {
      setNoPainReportData(true);
    } finally {
      setLoading(false);
    }
  };

  const checkIfReportLocked = () => {
    const ppcProductCodes = [ProductCode.PPCStandard, ProductCode.PPCAdvanced, ProductCode.PPCPremium];
    const customerPurchasedPPC = purchases?.orders?.some((order) =>
      order.purchaseItems.some((item) => ppcProductCodes.includes(item.variant as ProductCode))
    );

    if (customerPurchasedPPC) {
      setLocked(shouldUnlockByUrl ? !Number(unlockByUrl) : false);
    }
  };

  return { loading, locked, noPainReportData, painReportData };
};
